import AuthService from "../services/AuthService";

interface Config {
  rootUrl: string;
  tokenAccessor: () => string;
  includeSiteId?: boolean;
  /** When true, a 401 response triggers a full logout + loginRedirect. Only set for the admin API. */
  handleLogoutOn401?: boolean;
}

// ── Circuit Breaker ────────────────────────────────────────────────────────
// After FAILURE_THRESHOLD consecutive network errors the circuit opens and
// all requests fail immediately (no traffic to a dead API). After
// OPEN_DURATION_MS the circuit goes half-open for one probe; success closes
// it, another failure re-opens it.

type CircuitState = "closed" | "open" | "half-open";

const FAILURE_THRESHOLD = 3;
const OPEN_DURATION_MS = 30_000; // 30 s

// ── 401 Interceptor ────────────────────────────────────────────────────────
// When the admin API returns 401 the token has expired or been revoked. Clear
// stale tokens and kick off a fresh login. Guarded so concurrent requests
// during the expiry window don't trigger a redirect storm.
//
// Cross-reload loop guard: AuthService.loginRedirect() -> entraLoginRedirect()
// ends in window.location.replace(...) (a full hard reload) whenever MSAL's
// ssoSilent() succeeds -- which it will if the user's Entra session is
// genuinely fine. `_isRedirectingToLogin` below only protects a SINGLE page
// load (it's a plain module variable, reset to false by the very reload it's
// supposed to guard against). If a 401 keeps recurring for a reason that has
// nothing to do with session freshness (e.g. one endpoint's own scoped
// authorization check failing), a fresh token never fixes it: the next page
// load hits the same 401, clears the token, and reloads again -- forever.
// Confirmed live 2026-08-12 (QA report "Chatbot Training infinite reload"):
// the AI Training page reloaded every ~10-30s indefinitely because a single
// call kept 401ing on every fresh load for a reason unrelated to the admin's
// actual (valid) Entra session. sessionStorage survives the reload and caps
// how many times this can auto-loop before giving up loudly instead of
// spinning the tab forever -- same one-reload-per-window shape as
// staleBundleGuard.ts's guard.
const RELOGIN_RELOAD_GUARD_KEY = "velaro.401ReloginReloadAttempts";
const RELOGIN_RELOAD_GUARD_WINDOW_MS = 2 * 60_000;
const MAX_401_RELOGIN_RELOADS = 2;

/** Read the guard's stored state, tolerating corruption (missing/non-JSON/
 *  wrong-shaped payload) by treating it exactly like "no prior state" rather
 *  than propagating a parse exception into the caller's fail-open path below
 *  — a corrupted value must reset the window, not silently disable the cap
 *  forever (that would recreate the exact infinite-reload bug this guard
 *  exists to stop, just via a different trigger). */
function readReloginReloadGuardState(): {
  count: number;
  windowStart: number;
} | null {
  const raw = sessionStorage.getItem(RELOGIN_RELOAD_GUARD_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.count === "number" &&
      typeof parsed.windowStart === "number"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function shouldAttemptReloginReload(): boolean {
  try {
    const parsed = readReloginReloadGuardState();
    const now = Date.now();
    if (!parsed || now - parsed.windowStart > RELOGIN_RELOAD_GUARD_WINDOW_MS) {
      sessionStorage.setItem(
        RELOGIN_RELOAD_GUARD_KEY,
        JSON.stringify({ count: 1, windowStart: now })
      );
      return true;
    }
    if (parsed.count >= MAX_401_RELOGIN_RELOADS) return false;
    sessionStorage.setItem(
      RELOGIN_RELOAD_GUARD_KEY,
      JSON.stringify({
        count: parsed.count + 1,
        windowStart: parsed.windowStart
      })
    );
    return true;
  } catch {
    // sessionStorage.getItem/setItem itself threw (storage disabled/full) —
    // fail open so a genuinely expired session can still recover; the
    // in-memory _isRedirectingToLogin guard above still prevents a
    // same-page-load redirect storm regardless.
    return true;
  }
}

/** Called on every successful admin-API response so a real (one-off) 401 that
 *  gets fixed by the relogin doesn't leave the tab sitting on a stale "used
 *  up my reload budget" counter for the rest of the 2-minute window — the
 *  cap should only ever reflect CONSECUTIVE, unresolved failures. */
function clearReloginReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOGIN_RELOAD_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

let _isRedirectingToLogin = false;
function handle401(response: Response): Response {
  if (response.status === 401 && !_isRedirectingToLogin) {
    _isRedirectingToLogin = true;

    if (!shouldAttemptReloginReload()) {
      console.error(
        `[RestService] 401 received again after ${MAX_401_RELOGIN_RELOADS} automatic re-login ` +
          `reloads within ${RELOGIN_RELOAD_GUARD_WINDOW_MS / 1000}s — a fresh login did not fix it, ` +
          "so this is not a session-expiry issue. Stopping to avoid an infinite reload loop " +
          "instead of redirecting again. Reload manually or contact support if this persists."
      );
      return response;
    }

    console.warn(
      "[RestService] 401 received — session expired, redirecting to login"
    );
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("legacyToken");
      AuthService.token = { token: "", expires: new Date(Date.now() - 1000) };
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      AuthService.loginRedirect();
    }, 100);
  } else if (response.ok) {
    clearReloginReloadGuard();
  }
  return response;
}

// ImpersonationContextFilter.cs's ImpersonationFailedHeader constant.
const IMPERSONATION_FAILED_HEADER = "X-Impersonation-Failed";

/**
 * ImpersonationContextFilter (velaro-admin) returns 403 + X-Impersonation-Failed: 1 — NEVER
 * 401 — when a stored X-Support-Token can no longer be honored (expired, revoked, owner
 * mismatch, or a server-side resolve failure). It is deliberately never 401 because handle401
 * above treats ANY 401 from the admin API as "your own session expired" and forces a full
 * logout + login redirect — and getCommonHeaders() attaches whatever token is sitting in
 * sessionStorage to EVERY admin API call, so a 401 here would log a staff member out of their
 * own perfectly-valid Entra session on literally every subsequent request (including the
 * "revoke session" call they'd need to recover) until they manually reloaded the tab.
 *
 * For a safe, idempotent request (GET/HEAD) we transparently retry the SAME request once
 * without the token, so a read that happens to land mid-expiry doesn't break the page. For
 * anything that WRITES (POST/PUT/PATCH/DELETE) we do NOT auto-retry — silently replaying a
 * write without the impersonation header would land it on the caller's OWN site instead of the
 * target site with no visible sign anything went wrong, which is exactly the original incident
 * this fix exists to close (a config saved while "impersonating" one site actually landing on
 * another). Instead we clear the token and dispatch `supportSessionImpersonationFailed` so
 * SupportSessionBanner can show a clear, visible message telling the staff member their session
 * expired and the action did NOT go through — they must re-impersonate and retry manually. Any
 * other 403 (a real authorization denial unrelated to impersonation) is left completely alone.
 */
const SAFE_RETRY_METHODS = new Set(["GET", "HEAD"]);

async function recoverFromFailedImpersonation(
  input: string,
  init: RequestInit,
  signal: AbortSignal,
  response: Response
): Promise<Response> {
  if (
    response.status !== 403 ||
    response.headers.get(IMPERSONATION_FAILED_HEADER) !== "1"
  ) {
    return response;
  }
  const headers = init.headers as Record<string, string> | undefined;
  if (!headers || !("X-Support-Token" in headers)) {
    // This request never carried the header — an unrelated 403, leave it untouched.
    return response;
  }

  try {
    sessionStorage.removeItem("supportSessionToken");
    sessionStorage.removeItem("supportSession");
    window.dispatchEvent(new Event("supportSessionChanged"));
  } catch {
    /* ignore — storage disabled, nothing more we can do client-side */
  }

  const method = (init.method ?? "GET").toUpperCase();
  if (!SAFE_RETRY_METHODS.has(method)) {
    console.warn(
      `[RestService] Support session token could not be honored for a ${method} request — ` +
        "NOT auto-retrying a write. Surfacing the failure so the action is visibly not silently " +
        "dropped or replayed against the wrong site."
    );
    try {
      window.dispatchEvent(
        new CustomEvent("supportSessionImpersonationFailed", {
          detail: { method }
        })
      );
    } catch {
      /* ignore */
    }
    return response;
  }

  console.warn(
    "[RestService] Support session token could not be honored — clearing it and retrying " +
      "this safe (read-only) request once as the caller's own site."
  );
  const { "X-Support-Token": _dropped, ...retryHeaders } = headers;
  return fetch(input, { ...init, headers: retryHeaders, signal });
}

export class CircuitOpenError extends Error {
  readonly isCircuitOpen = true;
  constructor(rootUrl: string) {
    super(`API unreachable (${rootUrl}) — requests paused for 30 s`);
    this.name = "CircuitOpenError";
  }
}

interface CircuitBreakerState {
  state: CircuitState;
  failures: number;
  openedAt: number;
}

// ── GET Response Cache ─────────────────────────────────────────────────────
// Module-level so the cache persists across RestService instances within a
// browser session (one RestService is created per useApi() call, but we want
// all of them to share cached data).
//
// Only successful (2xx) GET responses are cached. Mutation methods
// (POST / PUT / PATCH / DELETE) automatically invalidate related entries so
// stale data is never served after a write.
//
// Timing:
//   FRESH_MS  — serve directly from cache, no network call at all.
//   SWR_MS    — serve stale data immediately + kick off a background refresh.
//               The UI updates silently when fresh data arrives; no spinner.
//
// In-flight deduplication:
//   Concurrent requests to the exact same URL share a single network call.
//   Each caller receives its own Response instance (the body string is copied,
//   not the readable stream, so each caller can call .json() independently).

interface CacheEntry {
  body: string;
  status: number;
  contentType: string;
  timestamp: number;
}

const _responseCache = new Map<string, CacheEntry>();
type ParsedEntry =
  { body: string; status: number; contentType: string } | { raw: Response };
const _inFlight = new Map<string, Promise<ParsedEntry>>();

/**
 * Content-types that must never be read via response.text() for caching — decoding
 * binary bytes as UTF-8 text and reconstructing a Response from the string is lossy
 * and silently corrupts non-text payloads (e.g. the Teams app package zip download).
 */
const BINARY_CONTENT_TYPE_RE =
  /^(application\/(zip|pdf|octet-stream|vnd\.[^;]+)|image\/|audio\/|video\/)/i;

const FRESH_MS = 30_000; // 30 s  — serve from cache, no network request
const SWR_MS = 5 * 60_000; // 5 min — serve stale + background refresh

/** Stable per-user cache key: URL + siteId prevents cross-user pollution. */
function _cacheKey(url: string): string {
  const siteId = AuthService.profile?.siteId ?? 0;
  return `${url}\x00${siteId}`;
}

/**
 * Invalidate all cache entries whose URL begins with the same "resource group"
 * as a mutated endpoint. This keeps lists fresh after any write.
 *
 * Derivation rule: use the first path segment, or `api/{second}` for api/ routes.
 *   "Teams/123"                → clears "…/Teams*"
 *   "api/SiteSettings/Save"    → clears "…/api/SiteSettings*"
 *   "api/kb/admin/domains/x"   → clears "…/api/kb*"
 */
function _invalidateRelated(rootUrl: string, endpoint: string): void {
  const parts = endpoint.split("/");
  const base =
    parts[0] === "api" && parts.length > 1 ? `api/${parts[1]}` : parts[0];
  const prefix = `${rootUrl}/${base}`;

  for (const key of _responseCache.keys()) {
    // Keys are "url\x00siteId" — compare only the URL portion.
    if (key.split("\x00")[0].startsWith(prefix)) {
      _responseCache.delete(key);
    }
  }
}

export default class RestService {
  rootUrl = "";
  tokenAccessor: () => string = () => "";
  includeSiteId: boolean = true;
  private _handleLogoutOn401: boolean = false;

  private _circuit: CircuitBreakerState = {
    state: "closed",
    failures: 0,
    openedAt: 0
  };

  constructor(config: Config) {
    this.rootUrl = config.rootUrl;
    this.tokenAccessor = config.tokenAccessor;
    this.includeSiteId = config.includeSiteId ?? true;
    this._handleLogoutOn401 = config.handleLogoutOn401 ?? false;
  }

  get accessToken() {
    return this.tokenAccessor();
  }

  getCommonHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.accessToken}`
    };
    if (this.includeSiteId) {
      headers.SiteId = AuthService.profile.siteId.toString();
    }
    const supportToken = sessionStorage.getItem("supportSessionToken");
    if (supportToken) {
      headers["X-Support-Token"] = supportToken;
    }
    return headers;
  }

  // ── Circuit-aware fetch ──────────────────────────────────────────────────

  private async _fetch(input: string, init: RequestInit): Promise<Response> {
    const now = Date.now();
    const c = this._circuit;

    if (c.state === "open") {
      if (now - c.openedAt >= OPEN_DURATION_MS) {
        c.state = "half-open";
      } else {
        // Fail fast — do NOT throw an unhandled rejection into the wild;
        // callers that don't catch will see CircuitOpenError rather than
        // "Failed to fetch", and GlobalErrorBoundary suppresses it.
        throw new CircuitOpenError(this.rootUrl);
      }
    }

    // Apply a default 60 s timeout so cold-starting APIs don't hang providers forever.
    // Long-running endpoints (PublishJob, large scrape ingest) pass their own
    // longer AbortSignal via init.signal and keep it.
    const signal = init.signal ?? AbortSignal.timeout(60_000);

    try {
      let response = await fetch(input, { ...init, signal });

      // Any response (even 4xx/5xx) means the network layer is healthy.
      if (c.state !== "closed") {
        this._circuit = { state: "closed", failures: 0, openedAt: 0 };
      } else {
        c.failures = 0;
      }

      response = await recoverFromFailedImpersonation(
        input,
        init,
        signal,
        response
      );

      return this._handleLogoutOn401 ? handle401(response) : response;
    } catch (err) {
      // A slow request that hit our own timeout is NOT a sign the API is dead —
      // it just means this one call is taking a while (e.g. PublishJob doing a
      // multi-minute ingest). Counting it as a network failure trips the circuit
      // and freezes unrelated polling (/version, /Subscription) for 30 s. Skip it.
      const isOurTimeout =
        err instanceof DOMException &&
        (err.name === "TimeoutError" || err.name === "AbortError");

      if (!isOurTimeout) {
        c.failures++;
        if (c.failures >= FAILURE_THRESHOLD || c.state === "half-open") {
          console.warn(
            `[RestService] Circuit OPEN for ${this.rootUrl} after ${c.failures} network failure(s).`,
            "Requests will be paused for 30 s."
          );
          this._circuit = {
            state: "open",
            failures: c.failures,
            openedAt: Date.now()
          };
        }
      }
      throw err;
    }
  }

  // ── Cached GET helpers ───────────────────────────────────────────────────

  /** Fetch a URL, read the full body text, and populate the cache on success.
   *  Binary content-types bypass text-decoding and caching entirely — see BINARY_CONTENT_TYPE_RE. */
  private async _fetchAndParse(
    url: string,
    init: RequestInit
  ): Promise<ParsedEntry> {
    const response = await this._fetch(url, init);
    const contentType =
      response.headers.get("Content-Type") ?? "application/json";

    if (BINARY_CONTENT_TYPE_RE.test(contentType)) {
      return { raw: response };
    }

    const body = await response.text();
    if (response.ok) {
      _responseCache.set(_cacheKey(url), {
        body,
        status: response.status,
        contentType,
        timestamp: Date.now()
      });
    }
    return { body, status: response.status, contentType };
  }

  /** Construct a fresh Response from a cache / parsed-fetch result.
   *  Each caller gets its own instance so .json() can be called independently. */
  private static _toResponse(entry: {
    body: string;
    status: number;
    contentType: string;
  }): Response {
    // 101, 103, 204, 205, 304 are "null body statuses" per the Fetch spec — passing any body throws.
    const nullBodyStatus = [101, 103, 204, 205, 304].includes(entry.status);
    return new Response(nullBodyStatus ? null : entry.body, {
      status: entry.status,
      headers: { "Content-Type": entry.contentType }
    });
  }

  // ── Public HTTP methods ──────────────────────────────────────────────────

  /**
   * GET with stale-while-revalidate caching + in-flight deduplication.
   *
   * FRESH  (age < 30 s)  — returned from cache; zero network calls.
   * STALE  (age < 5 min) — stale data returned immediately; background
   *                         refresh updates the cache silently (no spinner).
   * MISS   (age > 5 min) — normal fetch, deduplicated so N simultaneous
   *                         callers share one network request.
   *
   * Requests that carry a `data` body bypass caching (the URL alone is not
   * a sufficient key when the body varies).
   */
  get(endpoint: string, data?: any, signal?: AbortSignal): Promise<Response> {
    const url = `${this.rootUrl}/${endpoint}`;

    // Body-bearing GETs are uncommon; bypass the cache entirely.
    if (data != null) {
      return this._fetch(url, {
        method: "get",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
          ...this.getCommonHeaders()
        },
        signal
      });
    }

    const key = _cacheKey(url);
    const cached = _responseCache.get(key);
    const now = Date.now();

    // ── FRESH: serve from cache, no network call ──────────────────────────
    if (cached && now - cached.timestamp < FRESH_MS) {
      return Promise.resolve(RestService._toResponse(cached));
    }

    // Shared init for the actual network request.
    // The caller's signal is deliberately NOT forwarded so that one caller
    // aborting does not cancel a shared in-flight fetch used by others.
    // _fetch() applies the 15 s default timeout automatically.
    const fetchInit: RequestInit = {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        ...this.getCommonHeaders()
      }
    };

    // ── STALE: return immediately + silent background refresh ─────────────
    if (cached && now - cached.timestamp < SWR_MS) {
      this._fetchAndParse(url, fetchInit).catch(() => {
        /* stale data remains */
      });
      return Promise.resolve(RestService._toResponse(cached));
    }

    // ── MISS: deduplicate concurrent requests for the same URL ───────────
    const existing = _inFlight.get(key);
    if (existing) {
      return existing.then((entry) =>
        "raw" in entry ? entry.raw : RestService._toResponse(entry)
      );
    }

    const flight = this._fetchAndParse(url, fetchInit).finally(() =>
      _inFlight.delete(key)
    );
    _inFlight.set(key, flight);
    return flight.then((entry) =>
      "raw" in entry ? entry.raw : RestService._toResponse(entry)
    );
  }

  /**
   * Open a streaming GET connection (SSE / NDJSON). Unlike get(), this method:
   *  - Does NOT apply a timeout (streams are long-lived by design)
   *  - Does NOT cache or buffer the response body
   *  - Does NOT count toward the circuit-breaker failure threshold
   */
  stream(endpoint: string, signal?: AbortSignal): Promise<Response> {
    const url = `${this.rootUrl}/${endpoint}`;
    return fetch(url, {
      method: "get",
      headers: this.getCommonHeaders(),
      signal
    });
  }

  post(endpoint: string, data?: any, signal?: AbortSignal): Promise<Response> {
    _invalidateRelated(this.rootUrl, endpoint);
    return this._fetch(`${this.rootUrl}/${endpoint}`, {
      method: "post",
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...this.getCommonHeaders()
      },
      signal
    });
  }

  postFormData(endpoint: string, data: FormData): Promise<Response> {
    _invalidateRelated(this.rootUrl, endpoint);
    return this._fetch(`${this.rootUrl}/${endpoint}`, {
      method: "post",
      body: data,
      headers: { ...this.getCommonHeaders() }
    });
  }

  put(endpoint: string, data?: any): Promise<Response> {
    _invalidateRelated(this.rootUrl, endpoint);
    return this._fetch(`${this.rootUrl}/${endpoint}`, {
      method: "put",
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...this.getCommonHeaders()
      }
    });
  }

  patch(endpoint: string, data?: any): Promise<Response> {
    _invalidateRelated(this.rootUrl, endpoint);
    return this._fetch(`${this.rootUrl}/${endpoint}`, {
      method: "patch",
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...this.getCommonHeaders()
      }
    });
  }

  delete(endpoint: string, data?: any): Promise<Response> {
    _invalidateRelated(this.rootUrl, endpoint);
    return this._fetch(`${this.rootUrl}/${endpoint}`, {
      method: "delete",
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...this.getCommonHeaders()
      }
    });
  }
}
